import { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import { decodeImage } from "./decode";
import { applyFilterToCanvas, DEFAULT_FILTER, type Preset } from "./pixelFilters";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { applyImageFilter } from "./operations";

const filters: { name: string; value: Preset }[] = [
  { name: "None", value: "none" },
  { name: "Grayscale", value: "grayscale" },
  { name: "Sepia", value: "sepia" },
  { name: "Blur", value: "blur" },
  { name: "Invert", value: "invert" },
];

const TOOL_INFO_CONTENT = {
  what: `Applies visual filters and adjustments — brightness, contrast, saturation, black-and-white, and similar effects — to an image directly in your browser, calculated pixel by pixel so the result is identical on every browser, including Safari on iPhone. Useful for fixing a photo that came out too dark or washed out, giving a set of images a consistent look, or simply preparing an image for a specific use without opening a full photo editor.`,
  how: `Upload your image, pick a preset (Grayscale, Sepia, Blur, or Invert) and/or adjust the Brightness, Contrast, and Saturation sliders — a preset and the sliders stack together rather than one replacing the other, so a Sepia preset with a brightness boost applies both at once. Changes preview in real time, so you can see the effect before committing to it.`,
  tip: `Small adjustments to brightness and contrast together usually look more natural than pushing either one alone to an extreme — a photo that's too dark often just needs a modest brightness boost plus a slight contrast increase, rather than a large brightness change on its own, which tends to wash out the image instead of genuinely fixing it.`,
};

export default function ImageFilters() {
  const [file, setFile] = useState<File | null>(null);
  const [filter, setFilter] = useState<Preset>("none");
  const [intensity, setIntensity] = useState([100]);
  const [brightness, setBrightness] = useState([100]);
  const [contrast, setContrast] = useState([100]);
  const [saturation, setSaturation] = useState([100]);
  const [busy, setBusy] = useState(false);
  const srcRef = useRef<HTMLCanvasElement | null>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(0);

  const settings = () => ({ preset: filter, intensity: filter === "blur" ? Math.max(5, intensity[0]) : intensity[0], brightness: brightness[0], contrast: contrast[0], saturation: saturation[0] });

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    decodeImage(f).then(({ canvas }) => { srcRef.current = canvas; setFile(f); setReady((n) => n + 1); })
      .catch((e) => toast.error(e?.message || "This file could not be opened as an image"));
  }, []);

  // Preview: the same pixel maths as the download, on a copy up to 1200 px (larger than before).
  useEffect(() => {
    const src = srcRef.current, target = previewRef.current;
    if (!file || !src || !target) return;
    const t = setTimeout(() => {
      const out = applyFilterToCanvas(src, settings(), 1200);
      target.width = out.width; target.height = out.height;
      target.getContext("2d")!.drawImage(out, 0, 0);
    }, 40);
    return () => clearTimeout(t);
  }, [file, ready, filter, intensity, brightness, contrast, saturation]);

  const download = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const { blob, filename } = await applyImageFilter(file, settings());
      downloadBlob(blob, `filtered_${filename}`);
    } catch (e) { toast.error((e as Error)?.message || "Could not apply the filter"); }
    finally { setBusy(false); }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image" description="Apply filters and effects" />
      ) : (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden bg-card p-2">
            <canvas ref={previewRef} aria-label="Preview" className="max-h-[28rem] max-w-full mx-auto" />
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.map(f => (
              <Button key={f.name} variant={filter === f.value ? "default" : "outline"} size="sm" onClick={() => setFilter(f.value)}>{f.name}</Button>
            ))}
          </div>
          {(filter !== "none") && (
            <div className="max-w-xs"><Label>{filter === "blur" ? "Blur strength" : "Effect strength"}: {intensity[0]}%</Label><Slider value={intensity} onValueChange={setIntensity} min={filter === "blur" ? 5 : 0} max={100} /></div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div><Label>Brightness: {brightness[0]}%</Label><Slider value={brightness} onValueChange={setBrightness} min={50} max={200} /></div>
            <div><Label>Contrast: {contrast[0]}%</Label><Slider value={contrast} onValueChange={setContrast} min={50} max={200} /></div>
            <div><Label>Saturation: {saturation[0]}%</Label><Slider value={saturation} onValueChange={setSaturation} min={0} max={200} /></div>
          </div>
          <div className="flex gap-3">
            <DownloadButton onClick={download} markHint={`filtered_${file?.name ?? "image.png"}`} label={busy ? "Working..." : "Download"} />
            <Button variant="outline" onClick={() => { srcRef.current = null; setFile(null); setFilter("none"); setIntensity([100]); setBrightness([100]); setContrast([100]); setSaturation([100]); }}>Reset</Button>
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
